import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import Carousel from '@wsb/guac-widget-shared/lib/components/Carousel';
import wrapWithDeviceDetection from '../../common/wrapWithDeviceDetection';
import CategoryFilter from '../../common/components/categoryFilter';
import { hasCategories } from '../../common/helpers/categoryHelpers';
import CustomArrows from '../../common/components/customArrows';
import FeedCard from '../../common/components/feedCard';
import DataAid from '../../common/constants/data-aids';
import Field from '../../common/constants/editable-field-tags';
import { CARD_SLIDE_HEIGHT } from './constants';

const SLIDE_WIDTH = 245;
const CARD_INTER_SPACE = 25;
const NUM_CARDS_IN_CAROUSEL = {
  xs: 1,
  sm: 2,
  md: 3,
  lg: 4,
  xl: 4
};

const cardStyle = {
  container: {
    display: 'flex',
    textAlign: 'center',
    flexDirection: 'column',
    width: `${SLIDE_WIDTH}px`,
    marginHorizontal: `${CARD_INTER_SPACE / 2}px`
  },
  details: {
    fontSize: 'small' // Override theme
  },
  title: {
    'height': '3.45em',
    'width': '100%',
    'overflow': 'hidden',
    'position': 'relative',
    '@md': {
      height: '3.375em'
    },
    ':after': {
      content: '""',
      textAlign: 'right',
      position: 'absolute',
      bottom: '0',
      right: '0',
      width: '30%',
      height: '1.15em',
      background: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1) 100%)'
    }
  },
  content: {
    // Theme
    paddingHorizontal: 'small',
    paddingVertical: 'medium',
    // Static
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  background: {
    minHeight: '140px'
  },
  common: {}
};

class CardGroup extends Component {
  static get propTypes() {
    return {
      feeds: PropTypes.array.isRequired,
      locale: PropTypes.string,
      size: PropTypes.string.isRequired,
      device: PropTypes.string.isRequired,
      section: PropTypes.string,
      category: PropTypes.string,
      staticContent: PropTypes.object.isRequired,
      backgroundImage: PropTypes.string.isRequired,
      renderMode: PropTypes.string,
      cardClickHandler: PropTypes.func,
      categoryClickHandler: PropTypes.func,
      categories: PropTypes.array,
      selectedCategory: PropTypes.string,
      imgParams: PropTypes.string,
      viewDevice: PropTypes.string.isRequired,
      siteFontScale: PropTypes.string
    };
  }

  constructor() {
    super(...arguments);
    this.state = { currentSlide: 0 };
    this.handleSlideChange = this.handleSlideChange.bind(this);
  }

  handleSlideChange(newIndex) {
    this.setState({ currentSlide: newIndex });
  }

  getInitialSlidePosition(numberOfCardToShow) {
    switch (numberOfCardToShow) {
      case 1:
        return 0;
      case 2:
        return 1;
      case 3:
        return 1;
      case 4:
        return 2;
      default:
        return 2;
    }
  }

  getNumberOfCardsToShow() {
    const { device, size } = this.props;

    if (device === 'mobile') {
      return 1;
    }

    // desktop on mobile
    if (size === 'xs') {
      return 3;
    }

    return NUM_CARDS_IN_CAROUSEL[size] || 4;
  }

  renderCards() {
    const { feeds, staticContent, cardClickHandler, imgParams, viewDevice, locale, siteFontScale } =
      this.props;

    return feeds.map((feed, index) => (
      <FeedCard
        key={ `card-${index}` }
        feed={ feed }
        locale={ locale }
        isCard
        style={ cardStyle }
        showContent={ false }
        staticContent={ staticContent }
        dataAid={ `${DataAid.RSS_FEED_RENDERED}_${index}` }
        cardClickHandler={ cardClickHandler }
        imgParams={ imgParams }
        viewDevice={ viewDevice }
        showCategories={ false }
        siteFontScale={ siteFontScale }
      />
    ));
  }

  // eslint-disable-next-line complexity
  renderCardGroup() {
    const { section, category, size, device, backgroundImage, renderMode, feeds, siteFontScale } =
        this.props,
      { currentSlide } = this.state,
      numCardShow = this.getNumberOfCardsToShow(), // dynamic based on screen size
      showCarousel = feeds.length > numCardShow,
      shouldScaleDown = renderMode === 'EDIT' && device !== 'mobile' && numCardShow === 4,
      cardGroupStyle = {
        width: '100%',
        height: `${CARD_SLIDE_HEIGHT[siteFontScale]}px`,
        display: 'flex',
        textAlign: 'center',
        textShadow: 'none',
        flexDirection: 'row',
        justifyContent: 'center',
        overflow: 'hidden'
      };
    cardStyle.container.height = `${CARD_SLIDE_HEIGHT[siteFontScale]}px`;

    if (showCarousel) {
      // for even number of posts
      const trackLeftShift =
          numCardShow % 2 === 1
            ? {}
            : {
              position: 'relative',
              left: shouldScaleDown ? '108px' : `${(CARD_INTER_SPACE + SLIDE_WIDTH) / 2}px`
            },
        cardsWidth = (CARD_INTER_SPACE + SLIDE_WIDTH) * numCardShow - CARD_INTER_SPACE,
        controls = [],
        cardSizeRatio = shouldScaleDown ? 0.85 : 1;
      // temporary hack for lg screen
      let viewportWidth = ['xl', 'lg'].includes(size) ? '100%' : `${cardsWidth}px`;
      if (shouldScaleDown) {
        viewportWidth = `${Math.floor(cardsWidth * 0.85)}px`;
      }

      device === 'tablet' &&
        controls.push({
          component: CustomArrows
        });

      cardStyle.container.transform = `scale(${cardSizeRatio})`;
      cardStyle.title.transform = `scale(${cardSizeRatio})`;
      if (shouldScaleDown) {
        cardStyle.content.paddingLeft = '0';
      } else {
        delete cardStyle.content.paddingLeft;
      }

      const carouselProps = {
          initialSlide: this.getInitialSlidePosition(numCardShow),
          slideWidth: `${Math.floor((CARD_INTER_SPACE + SLIDE_WIDTH) * cardSizeRatio)}px`,
          slideHeight: `${CARD_SLIDE_HEIGHT[siteFontScale]}px`,
          viewportHeight: `${CARD_SLIDE_HEIGHT[siteFontScale]}px`,
          viewportWidth: viewportWidth,
          cellPadding: 0,
          numSlides: numCardShow,
          dots: false,
          arrows: false,
          autoplay: false,
          infinite: true,
          draggable: device === 'mobile',
          afterChange: this.handleSlideChange,
          controls: controls,
          clickToNavigate: false,
          lazyLoad: true,
          maxRenderedSlides: 7,
          style: {
            viewport: { margin: '0 auto' },
            track: trackLeftShift,
            slide: {
              opacity: '1',
              verticalAlign: 'top',
              marginTop: '0',
              marginBottom: '0'
            },
            selectedSlide: { opacity: '1' }
          }
        },
        counterProps = {
          section: backgroundImage ? 'overlay' : section,
          category: backgroundImage ? 'accent' : category
        },
        counterStyles = {
          style: {
            position: 'absolute',
            left: '50%',
            bottom: '-xlarge',
            transform: 'translateX(-50%)',
            display: 'block'
          }
        },
        blockStyle = {
          position: 'relative',
          paddingHorizontal: 'medium',
          textShadow: 'none',
          width: '100%',
          ...(device === 'mobile' ? { marginBottom: 'xlarge' } : {})
        };

      return (
        <UX2.Element.Block
          style={ blockStyle }
          data-aid={ DataAid.RSS_FEEDS_RENDERED }
          data-route={ Field.FEED_URL }
        >
          { size && <Carousel { ...carouselProps }>{ this.renderCards() }</Carousel> }
          { device === 'mobile' && (
            <UX2.Element.Text
              { ...counterProps }
              { ...counterStyles }
              data-aid={ DataAid.RSS_CAROUSEL_COUNTER }
              featured
            >
              { `${currentSlide + 1} / ${feeds.length}` }
            </UX2.Element.Text>
          ) }
        </UX2.Element.Block>
      );
    }

    return (
      <UX2.Element.Block
        style={ cardGroupStyle }
        data-aid={ DataAid.RSS_FEEDS_RENDERED }
        data-route={ Field.FEED_URL }
      >
        { this.renderCards() }
      </UX2.Element.Block>
    );
  }

  render() {
    const { categories, categoryClickHandler, selectedCategory, staticContent } = this.props;

    if (!hasCategories(categories)) {
      return this.renderCardGroup();
    }

    return (
      <UX2.Element.Block
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <UX2.Element.Block
          style={{
            'marginBottom': 'small',
            '@md': {
              marginBottom: 'large'
            }
          }}
        >
          <CategoryFilter
            categoryClickHandler={ categoryClickHandler }
            categories={ categories }
            selectedCategory={ selectedCategory }
            staticContent={ staticContent }
          />
        </UX2.Element.Block>
        { this.renderCardGroup() }
      </UX2.Element.Block>
    );
  }
}

export default wrapWithDeviceDetection(CardGroup);
